import React, { useState } from 'react';
import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Typography
} from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import authProvider from '../../provider/auth.provider';
import './ForgotPasswordDialog.css';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const getRecoveryErrorMessage = (error) => (
    error?.error?.message ||
    error?.message ||
    'No se ha podido recuperar la contraseña. Revisa los datos e inténtalo de nuevo.'
);

const ForgotPasswordDialog = ({ open, onClose }) => {
    const [email, setEmail] = useState('');
    const [identificationNumber, setIdentificationNumber] = useState('');
    const [loading, setLoading] = useState(false);
    const [feedback, setFeedback] = useState(null);
    const recoveryCompleted = feedback?.severity === 'success';

    const handleClose = () => {
        if (loading) {
            return;
        }

        setEmail('');
        setIdentificationNumber('');
        setFeedback(null);
        onClose();
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        const normalizedEmail = email.trim();
        const normalizedIdentificationNumber = identificationNumber.trim();

        if (!normalizedEmail || !normalizedIdentificationNumber) {
            setFeedback({
                severity: 'error',
                message: 'Introduce el correo electrónico y el usuario asociados a tu cuenta.'
            });
            return;
        }

        if (!EMAIL_PATTERN.test(normalizedEmail)) {
            setFeedback({
                severity: 'error',
                message: 'Introduce un correo electrónico válido.'
            });
            return;
        }

        setLoading(true);
        setFeedback(null);

        try {
            const response = await authProvider.forgotBackofficePassword(
                normalizedEmail,
                normalizedIdentificationNumber
            );
            setFeedback({
                severity: 'success',
                message: typeof response === 'string' && response.trim()
                    ? response
                    : 'La solicitud se ha procesado correctamente. Revisa tu correo electrónico.'
            });
        } catch (error) {
            setFeedback({
                severity: 'error',
                message: getRecoveryErrorMessage(error)
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            aria-labelledby="forgot-password-title"
            fullWidth
            maxWidth="xs"
        >
            <form onSubmit={handleSubmit} noValidate>
                <DialogTitle id="forgot-password-title">Recuperar contraseña</DialogTitle>
                <DialogContent>
                    <Typography className="forgot-password-description">
                        Introduce el correo electrónico y el usuario con el que accedes a la aplicación.
                    </Typography>
                    <TextField
                        autoFocus
                        autoComplete="email"
                        className="forgot-password-field"
                        disabled={loading || recoveryCompleted}
                        fullWidth
                        id="forgot-password-email"
                        label="Correo electrónico"
                        margin="normal"
                        onChange={(event) => setEmail(event.target.value)}
                        required
                        type="email"
                        value={email}
                        variant="outlined"
                    />
                    <TextField
                        autoComplete="username"
                        className="forgot-password-field"
                        disabled={loading || recoveryCompleted}
                        fullWidth
                        id="forgot-password-identification"
                        label="Usuario / DNI"
                        margin="normal"
                        onChange={(event) => setIdentificationNumber(event.target.value.toUpperCase())}
                        required
                        value={identificationNumber}
                        variant="outlined"
                    />
                    {feedback && (
                        <Alert className="forgot-password-feedback" severity={feedback.severity}>
                            {feedback.message}
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions className="forgot-password-actions">
                    <Button disabled={loading} onClick={handleClose} type="button">
                        Volver
                    </Button>
                    <Button
                        color="primary"
                        disabled={loading || recoveryCompleted}
                        type="submit"
                        variant="contained"
                    >
                        Recuperar contraseña
                        {loading && <CircularProgress className="forgot-password-progress" size={20} />}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default ForgotPasswordDialog;
